import { AdminPageHeader, AdminPageShell } from '@/components/patterns/admin-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Typography } from '@/components/ui/typography'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { canViewRolePermissions } from '@/lib/user-access'
import { roleQueries } from '@/lib/services/roles/roles.query'
import type { RoleDetail, RolePermission } from '@/lib/services/roles/roles.types'
import { userQueries } from '@/lib/services/users/users.query'
import { userSchema } from '@/lib/services/users/users.schema'
import { idSchema } from '@/lib/utils'
import { loadRouteDataOrNotFound } from '@/lib/route-loader'
import { createFileRoute, notFound } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Search, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

const rolePermissionsParamsSchema = userSchema.extend({
  roleId: idSchema('role'),
})

const ACTION_ORDER = [
  'list',
  'view',
  'create',
  'edit',
  'manage',
  'delete',
  'restore',
  'destroy',
  'assign',
  'access',
]

const HIGHEST_RISK_SUBJECTS = new Set([
  '*',
  'api-tokens',
  'contracts',
  'domains',
  'global',
  'global-roles',
  'global-users',
  'locations',
  'organizations',
  'roles',
])

const HIGH_RISK_SUBJECTS = new Set([
  'google-tag-manager',
  'meta-pixel',
  'organization-roles',
  'organization-users',
  'payouts',
  'products',
  'stripe-account',
  'transactions',
])

const GLOBAL_ONLY_SUBJECTS = new Set([
  '*',
  'api-tokens',
  'app-updates',
  'contracts',
  'global',
  'global-roles',
  'global-users',
  'organizations',
  'stock-images',
])

type RiskBucketId = 'highest-risk' | 'high-risk' | 'low-risk'

interface RiskBucket {
  id: RiskBucketId
  title: string
  description: string
}

interface ParsedPermissionName {
  action: string
  actionLabel: string
  subject: string
  subjectLabel: string
  field: string
  fieldLabel: string
}

export interface RolePermissionRow extends RolePermission, ParsedPermissionName {
  isAssigned: boolean
  isDirect: boolean
  sourceLabel: string
  statusLabel: string
}

interface RolePermissionSubject {
  subject: string
  subjectLabel: string
  bucketId: RiskBucketId
  permissions: RolePermissionRow[]
  assignedCount: number
  directCount: number
}

interface RolePermissionSection extends RiskBucket {
  subjects: RolePermissionSubject[]
}

const RISK_BUCKETS: RiskBucket[] = [
  {
    id: 'highest-risk',
    title: 'Highest Risk',
    description: 'Global access and permissions with the biggest blast radius.',
  },
  {
    id: 'high-risk',
    title: 'High Risk',
    description: 'Sensitive operational and financial permissions that deserve extra review.',
  },
  {
    id: 'low-risk',
    title: 'Low Risk',
    description: 'Lower-blast-radius permissions for day-to-day work.',
  },
]

const permissionSubjectColumns: ColumnDef<RolePermissionSubject>[] = [
  {
    accessorKey: 'subjectLabel',
    header: 'Subject',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{row.original.subjectLabel}</span>
    ),
    meta: { kind: 'primary', label: 'Subject' },
  },
  {
    id: 'permissions',
    header: 'Permissions',
    enableSorting: false,
    cell: ({ row }) => <PermissionBadges subject={row.original} />,
    meta: {
      kind: 'text',
      label: 'Permissions',
      minWidth: '20rem',
      preferredWidth: '44rem',
      contentClassName: 'flex flex-wrap gap-2 overflow-visible whitespace-normal',
    },
  },
]

export const Route = createFileRoute('/_authenticated/$orgId/users/$userId/roles/$roleId')({
  component: RouteComponent,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => rolePermissionsParamsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!canViewRolePermissions(context.ability, undefined)) {
      throw notFound()
    }

    const [role, user] = await loadRouteDataOrNotFound(
      () =>
        Promise.all([
          context.queryClient.ensureQueryData(
            roleQueries.show({
              organizationId: params.orgId,
              roleId: params.roleId,
            })
          ),
          context.queryClient.ensureQueryData(
            userQueries.show({
              organizationId: params.orgId,
              userId: params.userId,
            })
          ),
        ]),
      notFound
    )

    return {
      role,
      user,
      isReadOnly: true,
    }
  },
})

function formatPermissionPart(value: string, fallback = 'Any') {
  if (!value || value === '*') {
    return fallback
  }

  if (value === 'sales_orders' || value === 'sales-orders') {
    return 'Orders'
  }

  return value
    .split(/[-_:]/)
    .filter(Boolean)
    .map((part) =>
      part === 'contract' || part === 'contracts'
        ? 'Pricing'
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join(' ')
}

function parsePermissionName(name: string): ParsedPermissionName {
  const [action = '', subject = '', ...fieldParts] = String(name).split(':')

  return {
    action,
    actionLabel: formatPermissionPart(action),
    subject,
    subjectLabel: formatPermissionPart(subject, 'Everything'),
    field: fieldParts.join(':'),
    fieldLabel: fieldParts.length ? formatPermissionPart(fieldParts.join(' ')) : '',
  }
}

function getWildcardSource(
  permissionName: string,
  assignedPermissionNames: ReadonlySet<string>
): string | null {
  const { action, subject, field } = parsePermissionName(permissionName)
  const candidates = [
    field ? `${action}:${subject}` : null,
    field ? `${action}:${subject}:*` : null,
    subject ? `${action}:*` : null,
    subject ? `*:${subject}` : null,
    '*:*',
  ].filter((candidate): candidate is string => candidate !== null)

  return candidates.find((candidate) => assignedPermissionNames.has(candidate)) ?? null
}

function getPermissionSourceLabel(isDirect: boolean, source: string | null): string {
  if (isDirect) {
    return 'Direct'
  }

  if (!source) {
    return 'None'
  }

  return source.includes('*') ? `Wildcard: ${source}` : `Subject-wide: ${source}`
}

function getActionSortValue(action: string): number {
  const index = ACTION_ORDER.indexOf(action)

  return index === -1 ? ACTION_ORDER.length : index
}

function getPermissionBadgeLabel(row: RolePermissionRow): string {
  return row.fieldLabel ? `${row.actionLabel} ${row.fieldLabel}` : row.actionLabel
}

function getPermissionBadgeVariant(row: RolePermissionRow): BadgeVariant {
  if (row.isDirect) {
    return 'botanical'
  }

  if (row.isAssigned) {
    return 'periwinkle'
  }

  return 'muted'
}

function getRiskBucketId(subject: string): RiskBucketId {
  if (HIGHEST_RISK_SUBJECTS.has(subject)) {
    return 'highest-risk'
  }

  if (HIGH_RISK_SUBJECTS.has(subject)) {
    return 'high-risk'
  }

  return 'low-risk'
}

function shouldShowPermissionForRole(role: RoleDetail, permissionName: string): boolean {
  if (!permissionName) {
    return false
  }

  const parsed = parsePermissionName(permissionName)

  if (role.type === 'global') {
    return true
  }

  return parsed.action !== '*' && !GLOBAL_ONLY_SUBJECTS.has(parsed.subject)
}

export function buildRolePermissionRows(role: RoleDetail): RolePermissionRow[] {
  const assignedPermissionNames = new Set(
    role.permissions.map((permission) => permission.name).filter(Boolean)
  )

  return role.all_permissions
    .filter((permission) => shouldShowPermissionForRole(role, permission.name))
    .map((permission) => {
      const name = permission.name
      const parsed = parsePermissionName(name)
      const isDirect = assignedPermissionNames.has(name)
      const wildcardSource = isDirect ? null : getWildcardSource(name, assignedPermissionNames)
      const isAssigned = isDirect || Boolean(wildcardSource)

      return {
        ...permission,
        ...parsed,
        name,
        isAssigned,
        isDirect,
        sourceLabel: getPermissionSourceLabel(isDirect, wildcardSource),
        statusLabel: isAssigned ? 'Assigned' : 'Not assigned',
      }
    })
    .sort((a, b) => Number(b.isAssigned) - Number(a.isAssigned) || a.name.localeCompare(b.name))
}

function matchesSubjectSearch(subject: RolePermissionSubject, searchTerm: string): boolean {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase()

  if (!normalizedSearchTerm) {
    return true
  }

  return [
    subject.subject,
    subject.subjectLabel,
    ...subject.permissions.flatMap((permission) => [
      permission.name,
      getPermissionBadgeLabel(permission),
      permission.sourceLabel,
      permission.statusLabel,
    ]),
  ]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(normalizedSearchTerm))
}

function sortSubjectPermissions(a: RolePermissionRow, b: RolePermissionRow): number {
  return (
    Number(b.isAssigned) - Number(a.isAssigned) ||
    Number(b.isDirect) - Number(a.isDirect) ||
    getActionSortValue(a.action) - getActionSortValue(b.action) ||
    a.actionLabel.localeCompare(b.actionLabel) ||
    a.fieldLabel.localeCompare(b.fieldLabel) ||
    a.name.localeCompare(b.name)
  )
}

function sortSubjects(a: RolePermissionSubject, b: RolePermissionSubject): number {
  return a.subjectLabel.localeCompare(b.subjectLabel) || a.subject.localeCompare(b.subject)
}

function buildRolePermissionSubjects(role: RoleDetail): RolePermissionSubject[] {
  const subjects = new Map<string, RolePermissionSubject>()

  for (const permissionRow of buildRolePermissionRows(role)) {
    const existingSubject = subjects.get(permissionRow.subject)

    if (existingSubject) {
      existingSubject.permissions.push(permissionRow)
      existingSubject.assignedCount += Number(permissionRow.isAssigned)
      existingSubject.directCount += Number(permissionRow.isDirect)
      continue
    }

    subjects.set(permissionRow.subject, {
      subject: permissionRow.subject,
      subjectLabel: permissionRow.subjectLabel,
      bucketId: getRiskBucketId(permissionRow.subject),
      permissions: [permissionRow],
      assignedCount: Number(permissionRow.isAssigned),
      directCount: Number(permissionRow.isDirect),
    })
  }

  return [...subjects.values()]
    .map((subject) => ({
      ...subject,
      permissions: subject.permissions.sort(sortSubjectPermissions),
    }))
    .sort(sortSubjects)
}

function buildRolePermissionSections(
  role: RoleDetail,
  searchTerm: string
): RolePermissionSection[] {
  const visibleSubjects = buildRolePermissionSubjects(role).filter((subject) =>
    matchesSubjectSearch(subject, searchTerm)
  )

  return RISK_BUCKETS.map((bucket) => ({
    ...bucket,
    subjects: visibleSubjects.filter((subject) => subject.bucketId === bucket.id),
  })).filter((bucket) => bucket.subjects.length > 0)
}

function RoleMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-card px-4 py-3">
      <Typography variant="supporting" className="block">
        {label}
      </Typography>
      <Typography variant="h4" className="pb-0">
        {value}
      </Typography>
    </div>
  )
}

function PermissionBadges({ subject }: { subject: RolePermissionSubject }) {
  return (
    <>
      {subject.permissions.map((permission) => {
        const badgeVariant = getPermissionBadgeVariant(permission)

        return (
          <Badge
            key={permission.name}
            variant={badgeVariant}
            title={`${permission.name} - ${permission.sourceLabel}`}
          >
            {getPermissionBadgeLabel(permission)}
          </Badge>
        )
      })}
    </>
  )
}

export function RouteComponent() {
  const { role, user } = Route.useLoaderData()
  const [searchTerm, setSearchTerm] = useState('')
  const permissionRows = buildRolePermissionRows(role)
  const visibleSections = buildRolePermissionSections(role, searchTerm)
  const directCount = permissionRows.filter((row) => row.isDirect).length
  const effectiveCount = permissionRows.filter((row) => row.isAssigned).length
  const wildcardCount = permissionRows.filter((row) => row.isAssigned && !row.isDirect).length

  return (
    <AdminPageShell maxWidth="wide">
      <AdminPageHeader
        icon={ShieldCheck}
        title={role.display_name || role.name}
        description={role.summary || `Role permissions for ${user.name}`}
        badges={
          <>
            <Badge variant="outline">{role.name}</Badge>
            {role.type ? <Badge variant="muted">{formatPermissionPart(role.type)}</Badge> : null}
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <RoleMetric label="Direct permissions" value={directCount} />
        <RoleMetric label="Effective permissions" value={effectiveCount} />
        <RoleMetric label="Wildcard matches" value={wildcardCount} />
      </div>

      <Card>
        <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
          <CardTitle>
            <Typography variant="h4">Permissions</Typography>
          </CardTitle>
          <div className="relative w-full md:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search permissions"
              className="pl-9"
              value={searchTerm}
              placeholder="Search permissions"
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {visibleSections.length ? (
            visibleSections.map((section) => (
              <section key={section.id} className="space-y-3">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Typography variant="h4">{section.title}</Typography>
                    <Badge variant="outline">
                      {section.subjects.length} subject{section.subjects.length === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <Typography variant="supporting">{section.description}</Typography>
                </div>
                <IndexDataTable
                  mode="client"
                  data={section.subjects}
                  columns={permissionSubjectColumns}
                  getRowId={(subject) => subject.subject}
                  pagination={false}
                />
              </section>
            ))
          ) : (
            <EmptyState
              size="compact"
              icon={Search}
              title="No permissions matched"
              description="Try a different search term."
            />
          )}
        </CardContent>
      </Card>
    </AdminPageShell>
  )
}
